import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import {
  getProductCategories,
  getProducts,
  type Product,
  type ProductType,
  type L1Id,
  type L1Category,
} from "@/api/productApi";
import { assetUrl } from "@/utils/assetUrl";
import { getSolutionTree } from "@/api/solutionApi";
import { getAccentColor, getCategoryNavDisplay } from "@/utils/solutionDisplay";
import { ACTIVE_CATEGORY_SET } from "@/utils/solutionCategory";
import { useSEO, buildBreadcrumbLd } from "@/utils/seo";
import { readPreload, buildPreloadKey } from "@/api/preload";

/**
 * 把 `getProducts()` + `getSolutionTree()` + `getProductCategories()` 三份原始
 * 响应派生为页面所需的 `allProducts` / `l1Categories` / `subSolutions` 三份状态。
 * 供 `useEffect` 内的真实请求路径与首帧内嵌预载路径共用同一份派生逻辑
 * （需求 11.13：首帧输出与预渲染 HTML 完全一致）。
 */
function deriveProductListState(prods: any[], solTree: any[], cats: { l1Categories?: any[] }) {
  const tree = Array.isArray(solTree) ? solTree : [];

  // 1. 建立 subSolutionId → L1 categoryId 的映射
  const subIdToCategoryId = new Map<string, string>();
  // 2. 建立 productId → Set<subSolutionId> 的反向映射
  const productToSolutions = new Map<number, Set<string>>();
  // 3. 建立 productId → Set<L1 categoryId> 的映射（用于 L1 筛选）
  const productToL1 = new Map<number, Set<string>>();

  tree.forEach((cat: any) => {
    const catId = cat.categoryId || cat.id;
    (cat.subSolutions || []).forEach((sub: any) => {
      const solId = String(sub.id);
      subIdToCategoryId.set(solId, catId);
      (sub.relatedProductIds || []).forEach((pid: number) => {
        if (!productToSolutions.has(pid)) {
          productToSolutions.set(pid, new Set());
        }
        productToSolutions.get(pid)!.add(solId);
        if (!productToL1.has(pid)) {
          productToL1.set(pid, new Set());
        }
        productToL1.get(pid)!.add(catId);
      });
    });
  });

  const allProducts: Product[] = (prods || []).map((p: any) => {
    const solIds = productToSolutions.get(p.id);
    const l1Ids = productToL1.get(p.id);
    return {
      ...p,
      _solutionIds: solIds && solIds.size > 0 ? Array.from(solIds) : [],
      _l1Ids: l1Ids && l1Ids.size > 0 ? Array.from(l1Ids) : [],
    };
  });

  const l1Categories: L1Category[] = (cats.l1Categories || []).filter((c: any) => c.id !== "all");

  const subSolutions: { id: string; name: string; categoryId: string }[] = [];
  tree.forEach((cat: any) => {
    const catId = cat.categoryId || cat.id;
    (cat.subSolutions || []).forEach((sub: any) => {
      subSolutions.push({ id: String(sub.id), name: sub.name, categoryId: catId });
    });
  });

  // 产品大类筛选标签：分类集合与顺序来自接口（按 sort 升序），不再写死
  const solutionL1: L1Category[] = [...tree]
    .sort((a: any, b: any) => (a.sort ?? 0) - (b.sort ?? 0))
    .map((cat: any) => {
      const id = cat.categoryId || cat.id;
      return { id, name: getCategoryNavDisplay(id, { name: cat.tag || cat.title }).shortName };
    });

  return { allProducts, l1Categories, subSolutions, solutionL1 };
}

/** 首帧内嵌预载初值：三个接口全部命中时返回派生结果，任一缺失时返回 `null`（回退到默认空状态） */
function initialProductListState(): ReturnType<typeof deriveProductListState> | null {
  const prods = readPreload<any[]>(buildPreloadKey("GET", "/api/front/products", { pageNum: 1, pageSize: 9999 }));
  const solTree = readPreload<any[]>(buildPreloadKey("GET", "/api/front/solutions"));
  const cats = readPreload<{ l1Categories?: any[] }>(buildPreloadKey("GET", "/api/front/products/categories"));
  if (!Array.isArray(prods) || !Array.isArray(solTree) || !cats) return null;
  return deriveProductListState(prods, solTree, cats);
}

const productTypeLabels: Record<ProductType, string> = {
  hardware: "硬件",
  software: "软件",
  service: "服务",
};

/** 一级分类主题色："all" 为中性灰，其余取方案分类主题色（未配置分类自动回退灰色） */
const l1Color = (id: string): string => (id === "all" ? "#6b7280" : getAccentColor(id));

export default function ProductsPage() {
  const [searchParams] = useSearchParams();
  const [activeL1, setActiveL1] = useState<L1Id>("all");
  const [activeSub, setActiveSub] = useState<string>("all");
  const [activeTag, setActiveTag] = useState<string>("all");
  const filterRef = useRef<HTMLElement>(null);

  useSEO({
    title: "产品中心 | 信息安全 · IT基础架构 · 云信达 · AI业务 核心产品",
    description:
      "欧格登产品中心，汇集信息安全、IT基础架构、云信达、AI业务四大领域核心产品，包含主流品牌的硬件、软件与专业服务，为企业提供一站式选型与供应。",
    keywords: ["欧格登产品", "企业安全产品", "安全硬件", "安全软件", "IT产品", "云信达产品", "AI业务产品"],
    path: "/products",
    pageType: "PRODUCT_LIST",
    jsonLd: buildBreadcrumbLd([
      { name: "首页", path: "/" },
      { name: "产品中心", path: "/products" },
    ]),
  });

  // API data（首帧命中内嵌预载数据时直接以派生结果为初值，避免与预渲染 HTML 不一致，需求 11.13）
  const [initialState] = useState(initialProductListState);
  const [l1Categories, setL1Categories] = useState<L1Category[]>(() => initialState?.l1Categories ?? []);
  const [subSolutions, setSubSolutions] = useState<{ id: string; name: string; categoryId: string }[]>(
    () => initialState?.subSolutions ?? [],
  );
  const [allProducts, setAllProducts] = useState<Product[]>(() => initialState?.allProducts ?? []);
  const [solutionL1, setSolutionL1] = useState<L1Category[]>(() => initialState?.solutionL1 ?? []);
  const [loading, setLoading] = useState(() => initialState === null);
  const [error, setError] = useState<string | null>(null);

  // 产品大类筛选标签：“全部产品” + 接口返回的解决方案分类；接口返回前用生效分类集合占位
  const fixedL1: L1Category[] = [
    { id: "all" as L1Id, name: "全部产品" },
    ...(solutionL1.length > 0
      ? solutionL1
      : ACTIVE_CATEGORY_SET.map((id) => ({ id, name: getCategoryNavDisplay(id).shortName }))),
  ];

  // Load solutions and products
  useEffect(() => {
    setLoading(true);
    setError(null);
    Promise.all([getProducts(), getSolutionTree(), getProductCategories()])
      .then(([prods, solTree, cats]) => {
        const derived = deriveProductListState(prods, solTree, cats);
        setAllProducts(derived.allProducts);
        setL1Categories(derived.l1Categories);
        setSubSolutions(derived.subSolutions);
        setSolutionL1(derived.solutionL1);
      })
      .catch(() => {
        setError("加载产品数据失败，请稍后重试");
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  // URL param sync
  useEffect(() => {
    const solution = searchParams.get("solution");
    const subParam = searchParams.get("sub");
    const categoryParam = searchParams.get("category");
    const validL1: string[] = [...ACTIVE_CATEGORY_SET];

    if (solution && validL1.includes(solution)) {
      setActiveL1(solution as L1Id);
    }
    // 支持 ?sub=xxx 直接定位子方案
    if (subParam) {
      setActiveSub(subParam);
    } else if (solution) {
      // 切换大类时若未指定子方案，则重置为全部
      setActiveSub("all");
    }
    // 支持 ?category=xxx 直接定位细分类型
    if (categoryParam) {
      setActiveTag(categoryParam);
    } else if (solution && !subParam) {
      setActiveTag("all");
    }

    if (solution || subParam || categoryParam) {
      requestAnimationFrame(() => {
        if (filterRef.current) {
          const top = filterRef.current.getBoundingClientRect().top + window.scrollY - 80;
          window.scrollTo({ top, behavior: "smooth" });
        }
      });
    }
  }, [searchParams]);

  // 当前L1下的子方案
  const visibleSubs = activeL1 === "all"
    ? subSolutions
    : subSolutions.filter((s) => s.categoryId === activeL1);

  // Filter products
  const filtered = allProducts.filter((p) => {
    // L1 大类筛选：通过方案关联关系判断产品属于哪个大类
    const l1Ids: string[] = (p as any)._l1Ids || [];
    const l1Match = activeL1 === "all" || l1Ids.includes(activeL1);

    // 子方案筛选
    const solutionIds: string[] = (p as any)._solutionIds || [];
    const subMatch = activeSub === "all" || solutionIds.includes(activeSub);

    const tagMatch = activeTag === "all" || p.category === activeTag;
    return l1Match && subMatch && tagMatch;
  });

  // 从当前L1+子方案过滤后的产品中提取细分类型
  const productsForTags = allProducts.filter((p) => {
    const l1Ids: string[] = (p as any)._l1Ids || [];
    const l1Match = activeL1 === "all" || l1Ids.includes(activeL1);
    const solutionIds: string[] = (p as any)._solutionIds || [];
    const subMatch = activeSub === "all" || solutionIds.includes(activeSub);
    return l1Match && subMatch;
  });
  // 当前过滤范围内出现的产品类型（l1Categories中有对应category的）
  const usedCategoryIds = new Set(productsForTags.map((p) => p.category).filter(Boolean));
  const visibleTags = l1Categories.filter((c) => usedCategoryIds.has(c.id));

  const accentColor = l1Color(activeL1);

  const handleL1Change = (id: L1Id) => {
    setActiveL1(id);
    setActiveSub("all");
    setActiveTag("all");
  };

  const clearAll = () => {
    setActiveL1("all");
    setActiveSub("all");
    setActiveTag("all");
  };

  const hasFilter = activeL1 !== "all" || activeSub !== "all" || activeTag !== "all";

  if (loading) {
    return (
      <main className="min-h-screen bg-white">
        <section className="relative bg-[#0d1f3c] pt-32 pb-20 overflow-hidden">
          <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 text-center">
            <div className="h-6 w-20 bg-white/10 rounded-full mx-auto mb-5 animate-pulse" />
            <div className="h-10 w-96 bg-white/10 rounded mx-auto mb-5 animate-pulse" />
            <div className="h-5 w-80 bg-white/10 rounded mx-auto animate-pulse" />
          </div>
        </section>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12">
          <div className="h-10 bg-gray-100 rounded mb-8 animate-pulse" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div key={i} className="rounded-xl border border-gray-100 overflow-hidden">
                <div className="h-44 bg-gray-100 animate-pulse" />
                <div className="p-5 space-y-3">
                  <div className="h-4 w-16 bg-gray-100 rounded-full animate-pulse" />
                  <div className="h-4 w-3/4 bg-gray-100 rounded animate-pulse" />
                  <div className="h-3 w-1/2 bg-gray-100 rounded animate-pulse" />
                  <div className="h-3 w-full bg-gray-100 rounded animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="min-h-screen bg-white flex flex-col items-center justify-center">
        <div className="text-center mt-32">
          <i className="ri-wifi-off-line text-5xl text-gray-300 mb-4 block"></i>
          <h2 className="text-xl font-bold text-gray-600 mb-2">加载失败</h2>
          <p className="text-gray-400 text-sm mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2.5 bg-[#1a56db] text-white text-sm font-semibold rounded-full cursor-pointer whitespace-nowrap"
          >
            重新加载
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-white">
      {/* Hero */}
      <section className="relative bg-[#0d1f3c] pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://readdy.ai/api/search-image?query=enterprise%20security%20hardware%20products%20background%2C%20professional%20technology%20equipment%2C%20dark%20blue%20gradient%2C%20cybersecurity%20products%20showcase%2C%20clean%20minimal%20background&width=1920&height=600&seq=prodhero001&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0d1f3c]/60 to-[#0d1f3c]"></div>
        </div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 border border-white/20 rounded-full mb-5">
            <span className="text-white/80 text-xs font-medium">产品中心</span>
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold text-white mb-5">专业安全软硬件产品</h1>
          <p className="text-gray-400 text-base lg:text-lg max-w-2xl mx-auto">
            覆盖信息安全、IT基础架构、云信达、AI业务，为企业提供完整的产品体系。
          </p>
        </div>
      </section>

      {/* Filter Bar */}
      <section ref={filterRef} className="sticky top-16 lg:top-20 z-30 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">

          {/* 产品大类（三大解决方案） */}
          <div className="flex items-center gap-1.5 flex-wrap pt-3 pb-2 border-b border-gray-100">
            <span className="text-xs text-gray-400 mr-1 whitespace-nowrap font-medium">产品大类：</span>
            {fixedL1.map((l1) => {
              const color = l1Color(l1.id);
              const isActive = activeL1 === l1.id;
              return (
                <button
                  key={l1.id}
                  onClick={() => handleL1Change(l1.id as L1Id)}
                  className="px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-all duration-200 cursor-pointer border"
                  style={isActive
                    ? { backgroundColor: color, color: "#fff", borderColor: color }
                    : { backgroundColor: "transparent", color: "#6b7280", borderColor: "#e5e7eb" }
                  }
                >
                  {l1.name}
                </button>
              );
            })}
          </div>

          {/* 子方案（二级分类） */}
          {activeL1 !== "all" && visibleSubs.length > 0 && (
            <div className="flex items-start gap-2 py-2 border-b border-gray-50 flex-wrap">
              <span className="text-xs text-gray-400 whitespace-nowrap font-medium mt-1">子方案：</span>
              <div className="flex flex-wrap gap-1.5">
                <button
                  onClick={() => { setActiveSub("all"); setActiveTag("all"); }}
                  className="px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-all duration-200 cursor-pointer border"
                  style={activeSub === "all"
                    ? { backgroundColor: accentColor, color: "#fff", borderColor: accentColor }
                    : { backgroundColor: "transparent", color: "#6b7280", borderColor: "#e5e7eb" }
                  }
                >
                  全部
                </button>
                {visibleSubs.map((sub) => (
                  <button
                    key={sub.id}
                    onClick={() => { setActiveSub(sub.id === activeSub ? "all" : sub.id); setActiveTag("all"); }}
                    className="px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-all duration-200 cursor-pointer border"
                    style={activeSub === sub.id
                      ? { backgroundColor: accentColor, color: "#fff", borderColor: accentColor }
                      : { backgroundColor: "transparent", color: "#6b7280", borderColor: "#e5e7eb" }
                    }
                  >
                    {sub.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* 细分类型（产品tag） */}
          {visibleTags.length > 0 && (
            <div className="flex items-start gap-2 py-2 flex-wrap">
              <span className="text-xs text-gray-400 whitespace-nowrap font-medium mt-1">细分类型：</span>
              <div className="flex flex-wrap gap-1.5">
                <button
                  onClick={() => setActiveTag("all")}
                  className="px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-all duration-200 cursor-pointer border"
                  style={activeTag === "all"
                    ? { backgroundColor: accentColor, color: "#fff", borderColor: accentColor }
                    : { backgroundColor: "transparent", color: "#6b7280", borderColor: "#e5e7eb" }
                  }
                >
                  全部
                </button>
                {visibleTags.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => setActiveTag(c.id === activeTag ? "all" : c.id)}
                    className="px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-all duration-200 cursor-pointer border"
                    style={activeTag === c.id
                      ? { backgroundColor: accentColor, color: "#fff", borderColor: accentColor }
                      : { backgroundColor: "transparent", color: "#6b7280", borderColor: "#e5e7eb" }
                    }
                  >
                    {c.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-12 lg:py-16">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-3 mb-6">
            <p className="text-sm text-gray-400">
              共 <span className="font-semibold text-[#0d1f3c]">{filtered.length}</span> 款产品
            </p>
            {hasFilter && (
              <button
                onClick={clearAll}
                className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-close-circle-line text-xs"></i>
                </div>
                清除筛选
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {filtered.map((product) => {
              const types = Array.isArray(product.type) ? product.type : (product.type ? [product.type] : []);
              const firstL1 = ((product as any)._l1Ids || [])[0];
              const cardAccent = firstL1 ? l1Color(firstL1) : accentColor;
              return (
                <Link
                  key={product.id}
                  to={`/products/${product.id}`}
                  className="group bg-white rounded-xl border border-gray-100 hover:border-gray-200 overflow-hidden hover:-translate-y-1 transition-all duration-300 cursor-pointer flex flex-col"
                >
                  <div className="w-full h-44 overflow-hidden bg-[#f8fafc] flex-shrink-0 relative">
                    <img
                      src={assetUrl(product.image)}
                      alt={product.name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 right-3 flex gap-1">
                      {types.map((t) => {
                        const typeColor = t === "hardware" ? "#0891b2" : t === "software" ? "#c0392b" : "#16a34a";
                        return (
                          <span
                            key={t}
                            className="px-2 py-0.5 rounded-full text-white text-xs font-medium"
                            style={{ backgroundColor: `${typeColor}dd` }}
                          >
                            {productTypeLabels[t]}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <span
                      className="inline-block px-2.5 py-0.5 text-xs font-medium rounded-full mb-3 w-fit"
                      style={{ backgroundColor: `${cardAccent}12`, color: cardAccent }}
                    >
                      {product.tag}
                    </span>
                    <h3 className="text-sm font-bold text-[#0d1f3c] mb-1 leading-snug">{product.name}</h3>
                    <p className="text-xs text-gray-400 mb-3">{product.model}</p>
                    <p className="text-xs text-gray-500 leading-relaxed line-clamp-2 mb-4 flex-1">{product.desc}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {product.highlights.slice(0, 2).map((h) => (
                        <span key={h} className="px-2 py-0.5 bg-gray-50 text-gray-500 text-xs rounded-full border border-gray-100">
                          {h}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="px-5 pb-5">
                    <div
                      className="w-full py-2 border text-xs font-medium rounded-lg transition-all duration-200 text-center whitespace-nowrap"
                      style={{ borderColor: `${cardAccent}40`, color: cardAccent }}
                    >
                      查看详情
                      <i className="ri-arrow-right-line ml-1 text-xs group-hover:translate-x-0.5 inline-block transition-transform"></i>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-24">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-gray-50">
                <i className="ri-search-line text-2xl text-gray-300"></i>
              </div>
              <p className="text-gray-400 text-sm">暂无符合条件的产品</p>
              <button onClick={clearAll} className="mt-4 text-xs text-gray-500 cursor-pointer hover:underline">
                清除筛选条件
              </button>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-20 bg-[#F4F7FC]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 text-center">
          <h2 className="text-2xl lg:text-3xl font-bold text-[#0d1f3c] mb-4">找不到合适的产品？</h2>
          <p className="text-sm mb-7 text-[#555555]">告诉我们您的需求，我们将为您推荐最适合的产品组合方案</p>
          <a
            href="/#contact-form"
            className="inline-block px-10 py-3.5 bg-[#0d1f3c] hover:bg-[#1a3a6b] text-white font-semibold rounded-full transition-all duration-200 text-sm whitespace-nowrap cursor-pointer"
          >
            联系产品顾问
          </a>
        </div>
      </section>
    </main>
  );
}
